// src/evaluation/use-feature-flag.ts
import {
  ProviderEvents as ProviderEvents3,
  ProviderStatus as ProviderStatus2
} from "@openfeature/web-sdk";
import { useEffect as useEffect2, useState as useState2 } from "react";

// src/common/options.ts
var DEFAULT_OPTIONS = {
  updateOnContextChanged: true,
  updateOnConfigurationChanged: true,
  suspendUntilReady: true,
  suspendWhileReconciling: true
};
var normalizeOptions = (options = {}) => {
  const updateOnContextChanged = options.updateOnContextChanged;
  const updateOnConfigurationChanged = options.updateOnConfigurationChanged;
  const suspendUntilReady2 = "suspendUntilReady" in options ? options.suspendUntilReady : options.suspend;
  const suspendWhileReconciling = "suspendWhileReconciling" in options ? options.suspendWhileReconciling : options.suspend;
  return {
    // only return these if properly set (no undefined to allow overriding with spread)
    ...typeof suspendUntilReady2 === "boolean" && { suspendUntilReady: suspendUntilReady2 },
    ...typeof suspendWhileReconciling === "boolean" && { suspendWhileReconciling },
    ...typeof updateOnContextChanged === "boolean" && { updateOnContextChanged },
    ...typeof updateOnConfigurationChanged === "boolean" && { updateOnConfigurationChanged }
  };
};

// src/common/suspense.ts
import { ProviderEvents } from "@openfeature/web-sdk";
function suspendUntilReady(client) {
  let resolve;
  let reject;
  throw new Promise((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
    client.addHandler(ProviderEvents.Ready, resolve);
    client.addHandler(ProviderEvents.Error, reject);
  }).finally(() => {
    client.removeHandler(ProviderEvents.Ready, resolve);
    client.removeHandler(ProviderEvents.Ready, reject);
  });
}

// src/provider/context.ts
import React from "react";
var Context = React.createContext(void 0);
function useProviderOptions() {
  const { options } = React.useContext(Context) || {};
  return normalizeOptions(options);
}

// src/provider/use-open-feature-client.ts
import React2 from "react";
function useOpenFeatureClient() {
  const { client } = React2.useContext(Context) || {};
  if (!client) {
    throw new Error(
      "No OpenFeature client available - components using OpenFeature must be wrapped with an <OpenFeatureProvider>"
    );
  }
  return client;
}

// src/provider/use-open-feature-client-status.ts
import { useEffect, useState } from "react";
import { ProviderEvents as ProviderEvents2 } from "@openfeature/web-sdk";
function useOpenFeatureClientStatus() {
  const client = useOpenFeatureClient();
  const [status, setStatus] = useState(client.providerStatus);
  useEffect(() => {
    const updateStatus = () => setStatus(client.providerStatus);
    client.addHandler(ProviderEvents2.ConfigurationChanged, updateStatus);
    client.addHandler(ProviderEvents2.ContextChanged, updateStatus);
    client.addHandler(ProviderEvents2.Error, updateStatus);
    client.addHandler(ProviderEvents2.Ready, updateStatus);
    client.addHandler(ProviderEvents2.Stale, updateStatus);
    client.addHandler(ProviderEvents2.Reconciling, updateStatus);
    return () => {
      client.removeHandler(ProviderEvents2.ConfigurationChanged, updateStatus);
      client.removeHandler(ProviderEvents2.ContextChanged, updateStatus);
      client.removeHandler(ProviderEvents2.Error, updateStatus);
      client.removeHandler(ProviderEvents2.Ready, updateStatus);
      client.removeHandler(ProviderEvents2.Stale, updateStatus);
      client.removeHandler(ProviderEvents2.Reconciling, updateStatus);
    };
  }, [client]);
  return status;
}

// src/evaluation/hook-flag-query.ts
import {
  StandardResolutionReasons
} from "@openfeature/web-sdk";
var HookFlagQuery = class {
  constructor(_details) {
    this._details = _details;
  }
  get details() {
    return this._details;
  }
  get value() {
    return this._details?.value;
  }
  get variant() {
    return this._details.variant;
  }
  get flagMetadata() {
    return this._details.flagMetadata;
  }
  get reason() {
    return this._details.reason;
  }
  get isError() {
    return !!this._details?.errorCode || this._details.reason == StandardResolutionReasons.ERROR;
  }
  get errorCode() {
    return this._details?.errorCode;
  }
  get errorMessage() {
    return this._details?.errorMessage;
  }
  get isAuthoritative() {
    return !this.isError && this._details.reason != StandardResolutionReasons.STALE && this._details.reason != StandardResolutionReasons.DISABLED;
  }
  get type() {
    return typeof this._details.value;
  }
};

// src/evaluation/use-feature-flag.ts
function useFlag(flagKey, defaultValue, options) {
  const query = typeof defaultValue === "boolean" ? new HookFlagQuery(useBooleanFlagDetails(flagKey, defaultValue, options)) : typeof defaultValue === "number" ? new HookFlagQuery(useNumberFlagDetails(flagKey, defaultValue, options)) : typeof defaultValue === "string" ? new HookFlagQuery(useStringFlagDetails(flagKey, defaultValue, options)) : new HookFlagQuery(useObjectFlagDetails(flagKey, defaultValue, options));
  return query;
}
function useBooleanFlagValue(flagKey, defaultValue, options) {
  return useBooleanFlagDetails(flagKey, defaultValue, options).value;
}
function useBooleanFlagDetails(flagKey, defaultValue, options) {
  return attachHandlersAndResolve(
    flagKey,
    defaultValue,
    (client) => {
      return client.getBooleanDetails;
    },
    options
  );
}
function useStringFlagValue(flagKey, defaultValue, options) {
  return useStringFlagDetails(flagKey, defaultValue, options).value;
}
function useStringFlagDetails(flagKey, defaultValue, options) {
  return attachHandlersAndResolve(
    flagKey,
    defaultValue,
    (client) => {
      return client.getStringDetails;
    },
    options
  );
}
function useNumberFlagValue(flagKey, defaultValue, options) {
  return useNumberFlagDetails(flagKey, defaultValue, options).value;
}
function useNumberFlagDetails(flagKey, defaultValue, options) {
  return attachHandlersAndResolve(
    flagKey,
    defaultValue,
    (client) => {
      return client.getNumberDetails;
    },
    options
  );
}
function useObjectFlagValue(flagKey, defaultValue, options) {
  return useObjectFlagDetails(flagKey, defaultValue, options).value;
}
function useObjectFlagDetails(flagKey, defaultValue, options) {
  return attachHandlersAndResolve(
    flagKey,
    defaultValue,
    (client) => {
      return client.getObjectDetails;
    },
    options
  );
}
function attachHandlersAndResolve(flagKey, defaultValue, resolver, options) {
  const defaultedOptions = { ...DEFAULT_OPTIONS, ...useProviderOptions(), ...normalizeOptions(options) };
  const client = useOpenFeatureClient();
  const status = useOpenFeatureClientStatus();
  if (defaultedOptions.suspendUntilReady && status === ProviderStatus2.NOT_READY) {
    suspendUntilReady(client);
  }
  if (defaultedOptions.suspendWhileReconciling && status === ProviderStatus2.RECONCILING) {
    suspendUntilReady(client);
  }
  const [evalutationDetails, setEvaluationDetails] = useState2(
    resolver(client).call(client, flagKey, defaultValue, options)
  );
  const updateEvaluationDetailsRef = () => {
    setEvaluationDetails(resolver(client).call(client, flagKey, defaultValue, options));
  };
  useEffect2(() => {
    if (status === ProviderStatus2.NOT_READY) {
      client.addHandler(ProviderEvents3.Ready, updateEvaluationDetailsRef);
    }
    if (defaultedOptions.updateOnContextChanged) {
      client.addHandler(ProviderEvents3.ContextChanged, updateEvaluationDetailsRef);
    }
    return () => {
      client.removeHandler(ProviderEvents3.Ready, updateEvaluationDetailsRef);
      client.removeHandler(ProviderEvents3.ContextChanged, updateEvaluationDetailsRef);
    };
  }, []);
  useEffect2(() => {
    if (defaultedOptions.updateOnConfigurationChanged) {
      client.addHandler(ProviderEvents3.ConfigurationChanged, updateEvaluationDetailsRef);
    }
    return () => {
      client.removeHandler(ProviderEvents3.ConfigurationChanged, updateEvaluationDetailsRef);
    };
  }, []);
  return evalutationDetails;
}

// src/provider/provider.tsx
import { OpenFeature } from "@openfeature/web-sdk";
import * as React3 from "react";
function OpenFeatureProvider({ client, domain, children, ...options }) {
  if (!client) {
    client = OpenFeature.getClient(domain);
  }
  return /* @__PURE__ */ React3.createElement(Context.Provider, { value: { client, options } }, children);
}

// src/provider/use-when-provider-ready.ts
import { ProviderStatus as ProviderStatus3 } from "@openfeature/web-sdk";
function useWhenProviderReady(options) {
  const client = useOpenFeatureClient();
  const status = useOpenFeatureClientStatus();
  const defaultedOptions = { ...DEFAULT_OPTIONS, ...useProviderOptions(), ...normalizeOptions(options) };
  if (defaultedOptions.suspendUntilReady && status === ProviderStatus3.NOT_READY) {
    suspendUntilReady(client);
  }
  return status === ProviderStatus3.READY;
}

// src/index.ts
export * from "@openfeature/web-sdk";
export {
  OpenFeatureProvider,
  useBooleanFlagDetails,
  useBooleanFlagValue,
  useFlag,
  useNumberFlagDetails,
  useNumberFlagValue,
  useObjectFlagDetails,
  useObjectFlagValue,
  useOpenFeatureClient,
  useStringFlagDetails,
  useStringFlagValue,
  useWhenProviderReady
};
//# sourceMappingURL=index.js.map
